import {formatInTimeZone} from "date-fns-tz";
import {enGB} from "date-fns/locale/en-GB";
import HighchartsReact from "highcharts-react-official";
import {useMemo} from "react";

import {useCurrentOrPublicUserContext} from "@/components/context/hooks.ts";
import Highcharts from "@/components/Highcharts";
import {MetricGranularity, MetricSeries} from "@/lib/types/metricsExplorer.ts";
import {ChartEmptyState} from "@/pages/metricsExplorer/charts/ChartEmptyState.tsx";
import {
  buildChartSeries,
  collectUnits,
  formatAxisValue,
  formatMetricValue,
  gapSizeFor,
  isStacked,
} from "@/pages/metricsExplorer/chartSeries.ts";
import {DowntimeWindow} from "@/pages/metricsExplorer/chartShapes.ts";
import {ExplorerChartType} from "@/pages/metricsExplorer/explorerConfig.ts";

import styles from "./TimeSeriesChart.module.scss";

interface TimeSeriesChartProps {
  series: MetricSeries[];
  chartType: ExplorerChartType;
  checkNames: Record<number, string>;
  /** The bucket size the server actually used, which sets where lines break. */
  granularity: MetricGranularity;
  /** The selected window. The axis is held to it rather than to whatever data came back. */
  start: Date;
  end: Date;
  downtime?: DowntimeWindow[];
  emptyMessage?: string;
}

interface TooltipPointContext {
  x: number;
  y: number | null;
  color: string;
  series: {
    name: string;
    options: {custom?: {unit: string | null; realOffsetSeconds: number}};
  };
}

const MILLISECONDS_PER_SECOND = 1000;

const DOWNTIME_COLOR = "rgba(206, 5, 0, 0.15)";

/**
 * Metrics plotted over time: line, area and bar variants, plus the analysis overlays.
 *
 * Takes series, a chart type and a window — no queries, no config state — so a dashboard
 * widget can render a saved chart later by handing it the same three things.
 */
export const TimeSeriesChart = ({
  series,
  chartType,
  checkNames,
  granularity,
  start,
  end,
  downtime = [],
  emptyMessage = "Add a check and a metric to draw a chart.",
}: TimeSeriesChartProps) => {
  const {timezone} = useCurrentOrPublicUserContext();

  const chartSeries = useMemo(() => buildChartSeries({series, chartType, checkNames}), [series, chartType, checkNames]);

  const units = useMemo(() => collectUnits(series), [series]);

  const options = useMemo(
    () => ({
      title: {text: ""},
      credits: {enabled: false},
      chart: {
        zooming: {type: "x"},
        time: {timezone},
      },
      plotOptions: {
        series: {
          stacking: isStacked(chartType) ? "normal" : undefined,
          // Break the line where a bucket is missing rather than drawing across it.
          gapSize: gapSizeFor(granularity),
          gapUnit: "value",
        },
      },
      xAxis: {
        type: "datetime",
        gridLineWidth: 1,
        // Held to the selected window rather than left to fit the data. A comparison series
        // is drawn shifted onto this window, so letting the axis follow the data would let
        // a period from a month ago decide how much of the chart the current one gets.
        min: start.getTime(),
        max: end.getTime(),
        // Shaded behind the data so a spike can be read against the outage that caused it.
        plotBands: downtime.map((window) => ({
          color: DOWNTIME_COLOR,
          from: window.from,
          to: window.to,
        })),
        labels: {
          formatter: ({value}: {value: number}) =>
            formatInTimeZone(new Date(value), timezone, "MMM dd HH:mm", {locale: enGB}),
        },
      },
      // One axis per unit, so a chart mixing seconds and bytes stays readable. Every axis
      // after the first sits on the right.
      yAxis: units.map((unit, index) => ({
        title: {text: unit ?? undefined},
        opposite: index > 0,
        labels: {
          formatter: ({value}: {value: number}) => formatAxisValue(value, unit),
        },
      })),
      tooltip: {
        shared: true,
        outside: true,
        formatter: function (this: {points?: TooltipPointContext[]; x: number}): string {
          const header = formatInTimeZone(new Date(this.x), timezone, "MMM dd, HH:mm", {
            locale: enGB,
          });
          const rows = (this.points ?? []).map((point) => {
            const custom = point.series.options.custom;
            const value = formatMetricValue(point.y, custom?.unit ?? null);
            // A comparison series was slid onto the primary axis to overlay it, so show
            // the timestamp it actually came from rather than the one it is drawn at.
            const offset = custom?.realOffsetSeconds ?? 0;
            const actual = offset
              ? ` <span class="pulse-color-gray-600">(${formatInTimeZone(
                  new Date(point.x - offset * MILLISECONDS_PER_SECOND),
                  timezone,
                  "MMM dd, HH:mm",
                  {locale: enGB}
                )})</span>`
              : "";
            return `<span style="color:${point.color}">●</span> ${point.series.name}: <b>${value}</b>${actual}<br/>`;
          });
          return `<span style="font-size: 12px">${header}</span><br/>${rows.join("")}`;
        },
      },
      legend: {enabled: true},
      series: chartSeries,
    }),
    [chartSeries, chartType, downtime, granularity, start, end, timezone, units]
  );

  if (series.length === 0) {
    return <ChartEmptyState title="Nothing to chart yet" message={emptyMessage} />;
  }

  return (
    <div className={styles.chart}>
      <HighchartsReact highcharts={Highcharts} options={options} />
    </div>
  );
};
